import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab
} from '@mui/material';
import { 
  RefreshCw,
  Info,
  Plus,
  Minus
} from 'lucide-react';
import GridLayout, { Responsive, WidthProvider } from 'react-grid-layout';
import axios from 'axios';
import { 
  GlassContainer, 
  GlassmorphicCard, 
  SectionTitle,
  BodyText,
  SecondaryText,
  glowPulse,
  energyFlowH,
  energyFlowV,
  energyPulse,
  techHoverPulse,
  hologramBorder
} from '../../styles/sharedStyles';
import * as LucideIcons from 'lucide-react';
import * as MdiIcons from '@mdi/js';
import Icon from '@mdi/react';

const ReactGridLayout = GridLayout;

const HomeDashboard = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [haStates, setHaStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [optimisticStates, setOptimisticStates] = useState({});
  const [climateDialogOpen, setClimateDialogOpen] = useState(false);
  const [selectedClimate, setSelectedClimate] = useState(null);
  const [targetTemp, setTargetTemp] = useState(21);

  // Track container width for responsive grid sizing
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [rooms]);

  // Ensure an active room is selected when rooms change
  useEffect(() => {
    if (rooms.length === 0) {
      setActiveRoomId(null);
      return;
    }
    if (!activeRoomId || !rooms.some(r => r.id === activeRoomId)) {
      setActiveRoomId(rooms[0].id);
    }
  }, [rooms, activeRoomId]);

  // Calculate responsive cell size based on container width
  const getCellSize = () => {
    if (isMobile) return 14;  // Even smaller for mobile
    if (isTablet) return 18;
    return 20;  // Default desktop size
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [roomsRes, haRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/floorplan/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${apiBaseUrl}/api/homeassistant/entities`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const roomsList = roomsRes.data || [];
      setRooms(roomsList);
      if (roomsList.length > 0) {
        setActiveRoomId(prev => (!prev || !roomsList.some(r => r.id === prev)) ? roomsList[0].id : prev);
      }

      const states = {};
      haRes.data.forEach(entity => {
        states[entity.entity_id] = entity;
      });
      setHaStates(states);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleToggle = async (entityId) => {
    if (!entityId) return;

    const domain = entityId.split('.')[0];
    
    if (domain === 'climate') {
      const entity = haStates[entityId];
      setSelectedClimate(entity);
      setTargetTemp(entity?.attributes?.temperature || 21);
      setClimateDialogOpen(true);
      return;
    }

    const currentState = optimisticStates[entityId] || haStates[entityId]?.state;
    const isOff = currentState === 'off';
    
    // Optimistic UI
    setOptimisticStates(prev => ({
      ...prev,
      [entityId]: isOff ? 'on' : 'off'
    }));

    try {
      await axios.post(`${apiBaseUrl}/api/homeassistant/control`, {
        entity_id: entityId,
        action: 'toggle'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh state after a short delay
      setTimeout(fetchData, 1000);
    } catch (err) {
      console.error('Failed to toggle entity', err);
      // Revert optimistic state
      setOptimisticStates(prev => {
        const next = { ...prev };
        delete next[entityId];
        return next;
      });
    }
  };

  const handleSetTemperature = async () => {
    if (!selectedClimate) return;

    const entityId = selectedClimate.entity_id;
    
    // Optimistic UI
    setOptimisticStates(prev => ({
      ...prev,
      [entityId]: { ...haStates[entityId], attributes: { ...haStates[entityId].attributes, temperature: targetTemp } }
    }));

    try {
      await axios.post(`${apiBaseUrl}/api/homeassistant/control`, {
        entity_id: entityId,
        action: 'set_temperature',
        data: { temperature: targetTemp }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClimateDialogOpen(false);
      setTimeout(fetchData, 1000);
    } catch (err) {
      console.error('Failed to set temperature', err);
      setOptimisticStates(prev => {
        const next = { ...prev };
        delete next[entityId];
        return next;
      });
    }
  };
  const formatSensorValue = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    // Format to 1 decimal place and remove trailing zeros
    return num.toFixed(1).replace(/\.0$/, '');
  };

  const renderItemIcon = (item, size, color) => {
    if (item.icon_type === 'custom') {
      return (
        <img 
          src={`/${item.icon_name}.png`} 
          alt={item.icon_name}
          style={{ 
            width: size, 
            height: size, 
            objectFit: 'contain'
          }}
        />
      );
    }
    if (item.icon_type === 'mdi' && MdiIcons[item.icon_name]) {
      return <Icon path={MdiIcons[item.icon_name]} size={size / 24} color={color} />;
    }
    const LucideIcon = LucideIcons[item.icon_name] || LucideIcons.HelpCircle;
    return <LucideIcon size={size} color={color} />;
  };

  const renderItem = (item, cellSize) => {
    const objectType = item.object_type || 'device';
    
      // Calculate responsive icon size based on cell size (30% smaller)
      const iconSizeMultiplier = isMobile ? 5.6 : (isTablet ? 7 : 8);
      const iconSize = Math.min(item.w, item.h) * iconSizeMultiplier;

    // Text label items
    if (objectType === 'text_label') {
      return (
        <Box
          key={item.id}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              color: item.color_hex || '#ffffff',
              fontWeight: 600,
              textAlign: 'center',
              fontSize: isMobile ? '0.65rem' : '0.8rem',
              lineHeight: 1.1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              px: 0.5,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            {item.friendly_name || 'Text Label'}
          </Typography>
        </Box>
      );
    }

    // Structural items (walls, doors, windows) - passive visuals
    if (['wall_h', 'wall_v', 'door_h', 'door_v', 'window_h', 'window_v', 'door', 'window', 'energy_pipe_h_green', 'energy_pipe_v_green', 'energy_pipe_h_red', 'energy_pipe_v_red'].includes(objectType)) {
      const color = item.color_hex || (objectType.startsWith('door') ? '#8B4513' : objectType.startsWith('window') ? '#87CEEB' : '#666666');
      
      const isVertical = objectType.endsWith('_v') || objectType.includes('_v_');
      const isWall = objectType.startsWith('wall_');
      const isDoor = objectType.startsWith('door');
      const isWindow = objectType.startsWith('window');
      const isEnergyPipe = objectType.startsWith('energy_pipe_');

      // Animation logic for energy pipes
      let isAnimated = false;
      if (isEnergyPipe) {
        if (item.trigger_sensor) {
          const stateObj = haStates[item.trigger_sensor];
          const state = stateObj?.state;
          const numValue = parseFloat(state);
          if (!isNaN(numValue)) {
            isAnimated = numValue > 0;
          } else {
            isAnimated = state === 'on' || state === 'active' || state === 'running';
          }
        } else {
          // Fallback to legacy hardcoded logic if no trigger sensor is set
          if (objectType.includes('_green')) {
            const mpptPower = parseFloat(haStates['sensor.victron_mppt_panel_power']?.state);
            isAnimated = !isNaN(mpptPower) && mpptPower > 0;
          } else if (objectType.includes('_red')) {
            isAnimated = true; // Red pipes are always on
          }
        }
      }

      const pipeColor = objectType.includes('_green') ? '#00ff88' : '#ff4444';
      const flowSpeed = item.flow_speed || 1.0;
      const isReverse = item.flow_direction === 'reverse';

      return (
        <Box
          key={item.id}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'default',
            position: 'relative',
            zIndex: 9999, // Extremely high to ensure it stays on top
          }}
        >
          {/* Main Pipe Container */}
          <Box sx={{
            bgcolor: isWall ? color : 'transparent',
            border: isWall ? 'none' : 'none',
            borderRadius: '10px',
            position: 'relative',
            overflow: 'visible', // Allow glow to spread
            backdropFilter: isEnergyPipe ? 'blur(8px)' : 'none',
            ...(isWall && (isVertical ? { 
              width: '6px', height: '100%', borderRadius: '3px',
              background: `linear-gradient(to right, ${color}, ${color}dd)`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.1)'
            } : { 
              height: '6px', width: '100%', borderRadius: '3px',
              background: `linear-gradient(to bottom, ${color}, ${color}dd)`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.1)'
            })),
            ...(isDoor && (isVertical ? { 
              width: '100%', height: '100%',
              '&::before': {
                content: '""', position: 'absolute', top: 0, right: 0, height: '100%', width: '4px',
                bgcolor: color, borderRadius: '2px', transform: 'rotate(-25deg)', transformOrigin: 'center top',
                boxShadow: '2px 0 4px rgba(0,0,0,0.3)', zIndex: 2
              },
              '&::after': {
                content: '""', position: 'absolute', top: 0, right: 0, width: '100%', height: '100%',
                border: `1.5px dashed ${color}80`, borderRadius: '0 0 0 100%', borderRight: 'none', borderTop: 'none'
              }
            } : { 
              height: '100%', width: '100%',
              '&::before': {
                content: '""', position: 'absolute', left: 0, bottom: 0, width: '100%', height: '4px',
                bgcolor: color, borderRadius: '2px', transform: 'rotate(-25deg)', transformOrigin: 'left center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)', zIndex: 2
              },
              '&::after': {
                content: '""', position: 'absolute', left: 0, bottom: 0, width: '100%', height: '100%',
                border: `1.5px dashed ${color}80`, borderRadius: '0 100% 0 0', borderLeft: 'none', borderBottom: 'none'
              }
            })),
            ...(isWindow && (isVertical ? { 
              width: '8px', height: '100%', 
              background: `linear-gradient(to right, ${color}80, ${color}20)`,
              border: `1.5px solid ${color}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 0 10px rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&::before': {
                content: '""', height: '40%', width: '1px', bgcolor: 'rgba(255,255,255,0.5)', transform: 'rotate(-45deg)'
              }
            } : { 
              height: '8px', width: '100%', 
              background: `linear-gradient(to bottom, ${color}80, ${color}20)`,
              border: `1.5px solid ${color}`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 0 10px rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&::before': {
                content: '""', width: '40%', height: '1px', bgcolor: 'rgba(255,255,255,0.5)', transform: 'rotate(-45deg)'
              }
            })),
            ...(isEnergyPipe && (isVertical ? { width: '12px', height: '100%', borderRadius: '10px' } : { height: '12px', width: '100%', borderRadius: '10px' })),
            
            // Energy Pipe specific styling
            ...(isEnergyPipe && {
              boxShadow: isAnimated 
                ? `0 0 25px ${pipeColor}40, inset 0 0 10px ${pipeColor}30` 
                : 'inset 0 0 5px rgba(255,255,255,0.05)',
              border: `1px solid ${isAnimated ? `${pipeColor}80` : 'rgba(255,255,255,0.1)'}`,
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }),

            // Energy flow animation overlay
            ...(isEnergyPipe && isAnimated && {
              '&::after': { // The flowing "liquid" energy
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                borderRadius: 'inherit',
                background: `repeating-linear-gradient(
                  ${isVertical ? (isReverse ? 'to top' : 'to bottom') : (isReverse ? 'to left' : 'to right')},
                  transparent,
                  transparent 15px,
                  ${pipeColor}60 20px,
                  ${pipeColor} 25px,
                  ${pipeColor}60 30px,
                  transparent 45px
                )`,
                backgroundSize: isVertical ? '100% 80px' : '80px 100%',
                animation: `${isVertical ? energyFlowV : energyFlowH} ${1.0 / flowSpeed}s linear infinite`,
                animationDirection: isReverse ? 'reverse' : 'normal',
                zIndex: 1
              },
              '&::before': { // Glass reflection / highlight
                content: '""',
                position: 'absolute',
                top: '5%', left: '10%', right: '10%', bottom: '60%',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)',
                borderRadius: '10px',
                zIndex: 2,
                pointerEvents: 'none'
              }
            })
          }} />
        </Box>
      );
    }

    const stateObj = item.entity_id ? haStates[item.entity_id] : null;
    const state = item.entity_id ? (optimisticStates[item.entity_id] || stateObj?.state || 'unknown') : 'on';
    const isUnavailable = item.entity_id ? (state === 'unavailable' || state === 'unknown') : false;
    const isActive = item.entity_id ? (state === 'on' || (state !== 'off' && !isUnavailable)) : true;
    const color = item.color_hex || '#00d4ff';

    const isSensor = item.entity_id?.startsWith('sensor.');
    const isClimate = item.entity_id?.startsWith('climate.');
    const isLight = item.icon_name === 'Lightbulb' && isActive;
    
    let displayValue = '';
    if (isSensor) {
      if (isUnavailable) {
        displayValue = 'Offline';
      } else {
        const formattedState = formatSensorValue(state);
        displayValue = `${formattedState}${stateObj?.attributes?.unit_of_measurement || ''}`;
      }
    } else if (isClimate) {
      if (isUnavailable) {
        displayValue = 'Offline';
      } else {
        const current = stateObj?.attributes?.current_temperature;
        const target = stateObj?.attributes?.temperature;
        displayValue = `${formatSensorValue(current) || '?'}/${formatSensorValue(target) || '?'}°C`;
      }
    }

    return (
      <Box 
        key={item.id}
        onClick={() => !isSensor && item.entity_id && handleToggle(item.entity_id)}
        sx={{ 
          width: '100%',
          height: '100%',
          bgcolor: isActive ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(12px)',
          border: `1px solid ${isActive ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
          borderRadius: isMobile ? '12px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          cursor: (isSensor || !item.entity_id) ? 'default' : 'pointer',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          position: 'relative',
          overflow: 'visible',
          // Glow effect instead of hard border shadow
          ...(isActive && item.glow_enabled !== false && {
            boxShadow: `0 0 30px ${color}22, inset 0 0 10px ${color}11`,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -2, left: -2, right: -2, bottom: -2,
              borderRadius: 'inherit',
              background: `linear-gradient(135deg, ${color}44, transparent, ${color}22)`,
              zIndex: -1,
              padding: '2px',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'exclude',
              opacity: 0.5,
            }
          }),
          '&:active': {
            transform: (isSensor || !item.entity_id) ? 'none' : 'scale(0.92)',
            bgcolor: 'rgba(255, 255, 255, 0.15)',
          },
          '&:hover': {
            bgcolor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.07)',
            transform: isMobile ? 'none' : ((isSensor || !item.entity_id) ? 'none' : 'scale(1.08) translateY(-4px)'),
            zIndex: 10,
            ...(isActive && item.glow_enabled !== false ? {
              boxShadow: `0 0 40px ${color}44, 0 0 80px ${color}22, inset 0 0 20px ${color}1a`,
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 'inherit',
                padding: '2px',
                background: `conic-gradient(from 0deg, ${color}99, ${color}44 20%, transparent 40%, ${color}44 60%, ${color}99 80%, ${color}44)`,
                animation: `${hologramBorder} 3s linear infinite`,
                zIndex: 1,
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'exclude',
                boxShadow: `0 0 20px ${color}cc`,
                opacity: 0.7,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                width: '140%',
                height: '140%',
                background: `radial-gradient(circle, ${color}28 0%, transparent 70%)`,
                animation: `${techHoverPulse} 2.5s ease-in-out infinite`,
                pointerEvents: 'none',
                zIndex: -1,
              },
            } : {
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            }),
          },
          // Dynamic Ambient Light effect
          ...(isActive && item.glow_enabled !== false && {
            '&::before': {
              content: '""',
              position: 'absolute',
              width: '120%',
              height: '120%',
              background: `radial-gradient(circle, ${color}1a 0%, transparent 70%)`,
              borderRadius: '50%',
              pointerEvents: 'none',
              zIndex: -1,
            }
          })
        }}
      >
        <Box sx={{ 
          filter: (isActive && item.glow_enabled !== false) ? `drop-shadow(0 0 12px ${color}cc)` : 'none',
          color: isActive ? color : 'rgba(255,255,255,0.3)',
          transition: 'all 0.4s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isMobile ? 0 : 0.2,
          transform: isActive ? 'scale(1.1)' : 'scale(1)',
        }}>
          {renderItemIcon(item, iconSize, isActive ? color : 'inherit')}
          {(isSensor || isClimate) && (
            <Typography sx={{ 
              fontSize: isMobile ? '0.55rem' : '0.7rem', 
              fontWeight: 700,
              color: isUnavailable ? 'rgba(255,255,255,0.4)' : 'white',
              lineHeight: 1,
              mt: isMobile ? -0.2 : 0,
              textShadow: isUnavailable ? 'none' : '0 2px 4px rgba(0,0,0,0.5)',
              fontStyle: isUnavailable ? 'italic' : 'normal',
              opacity: isUnavailable ? 0.7 : 1
            }}>
              {displayValue}
            </Typography>
          )}
        </Box>
        {(item.entity_id || item.friendly_name) && (
          <Typography sx={{ 
            mt: isMobile ? 0.3 : 0.6,
            fontSize: isMobile ? '0.5rem' : '0.65rem', 
            fontWeight: 500,
            color: isActive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
            textAlign: 'center',
            px: 0.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: isMobile ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.1,
            width: '100%',
            letterSpacing: '0.01em'
          }}>
            {item.friendly_name || stateObj?.attributes?.friendly_name || ''}
          </Typography>
        )}
      </Box>
    );
  };

  if (loading && rooms.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
      {rooms.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={activeRoomId ?? rooms[0]?.id ?? false}
            onChange={(e, val) => setActiveRoomId(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: isMobile ? 36 : 40,
              '& .MuiTab-root': {
                minHeight: isMobile ? 36 : 40,
                textTransform: 'none',
                fontSize: isMobile ? '0.7rem' : '0.85rem',
                color: 'rgba(255,255,255,0.6)',
                '&.Mui-selected': { color: '#00d4ff' }
              },
              '& .MuiTabs-indicator': {
                background: 'linear-gradient(90deg, #00d4ff, #ff4081)',
                height: 2
              }
            }}
          >
            {rooms.map(room => (
              <Tab key={room.id} value={room.id} label={room.name} />
            ))}
          </Tabs>
          <IconButton onClick={fetchData} size="small" sx={{ color: '#00d4ff', ml: 1 }}>
            <RefreshCw size={18} />
          </IconButton>
        </Box>
      )}

      {rooms.length === 0 ? (
        <GlassmorphicCard sx={{ p: isMobile ? 3 : 4, textAlign: 'center' }}>
          <Info size={isMobile ? 32 : 48} color="#00d4ff" style={{ marginBottom: 16 }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>No rooms configured</Typography>
          <SecondaryText sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            Go to Room Designer to create your first room layout.
          </SecondaryText>
        </GlassmorphicCard>
      ) : (
        rooms
          .filter(room => {
            const selectedId = activeRoomId ?? rooms[0]?.id;
            return !selectedId || room.id === selectedId || room.id == selectedId;
          })
          .map(room => {
            const roomItems = room.items || [];
            const cellSize = getCellSize();
            const gridWidth = room.grid_width * cellSize;
            const gridHeight = room.grid_height * cellSize;
            
            // Maintain original room aspect ratio on desktop, allow scrolling if needed
            const effectiveWidth = isMobile ? (containerWidth > 0 ? Math.max(gridWidth, containerWidth - 16) : gridWidth) : gridWidth;
            
            return (
              <Box key={room.id} sx={{ mb: isMobile ? 3 : 4 }}>
                <Box sx={{ 
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  borderRadius: isMobile ? '20px' : '32px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(10px)',
                  position: 'relative',
                  p: isMobile ? 1 : 1.5,
                  boxShadow: 'inset 0 0 40px rgba(0,0,0,0.5)',
                  // Custom scrollbar for mobile
                  '&::-webkit-scrollbar': {
                    height: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '10px',
                  },
                  // Smooth scrolling
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  <Box sx={{ 
                    width: `${effectiveWidth}px`,
                    height: `${gridHeight}px`,
                    position: 'relative',
                    mx: isMobile ? 0 : 'auto',
                    minWidth: isMobile ? '280px' : 'auto',
                     ...(room.floor_type === 'concrete' ? {
                        backgroundColor: '#1a1a1a',
                        backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                      } : room.floor_type === 'tiles' ? {
                        backgroundColor: '#0a0a0a',
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
                        backgroundSize: '50px 50px',
                      } : room.floor_type === 'wood' ? {
                        backgroundColor: '#1a1410',
                        backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.2) 1px, transparent 1px)',
                        backgroundSize: '20px 100%',
                      } : room.floor_type === 'carpet' ? {
                        backgroundColor: '#1a1614',
                        backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)',
                        backgroundSize: '15px 15px',
                      } : room.floor_type === 'stone' ? {
                        backgroundColor: '#1a1c20',
                        backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.01) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.01) 75%, rgba(255,255,255,0.01)), linear-gradient(45deg, rgba(255,255,255,0.01) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.01) 75%, rgba(255,255,255,0.01))',
                        backgroundSize: '30px 30px',
                        backgroundPosition: '0 0, 15px 15px',
                      } : room.floor_type === 'vinyl' ? {
                        backgroundColor: '#241e18',
                        backgroundImage: 'linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)',
                        backgroundSize: '30px 100%',
                      } : {
                        backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                      }),
                      borderRadius: '16px',
                      overflow: 'hidden'
                  }}>
                    <ReactGridLayout
                      className="layout"
                      layout={roomItems.map(item => ({ i: item.id.toString(), x: item.x, y: item.y, w: item.w, h: item.h }))}
                      cols={room.grid_width}
                      width={effectiveWidth}
                      rowHeight={cellSize}
                      isDraggable={false}
                      isResizable={false}
                      margin={[0, 0]}
                      compactType={null}
                      verticalCompact={false}
                    >
                      {roomItems.map(item => {
                        const isPipe = item.object_type?.startsWith('energy_pipe_');
                        return (
                          <div key={item.id.toString()} style={{ zIndex: isPipe ? 9999 : 1, position: 'relative' }}>
                            {renderItem(item, cellSize)}
                          </div>
                        );
                      })}
                    </ReactGridLayout>
                  </Box>
                </Box>
              </Box>
            );
          })
      )}
      
      <Dialog 
        open={climateDialogOpen} 
        onClose={() => setClimateDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(10, 15, 25, 0.95)',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: isMobile ? 0 : '32px',
            color: 'white',
            backdropFilter: 'blur(24px)',
            minWidth: isMobile ? '100%' : 340,
            boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          fontSize: isMobile ? '1.2rem' : '1.5rem',
          fontWeight: 700,
          pt: isMobile ? 4 : 5,
          letterSpacing: '-0.02em'
        }}>
          {selectedClimate?.attributes?.friendly_name || 'Climate Control'}
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: isMobile ? 2 : 4, 
          gap: isMobile ? 3 : 5,
          px: isMobile ? 3 : 4
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 2 : 4,
            width: '100%',
            justifyContent: 'center'
          }}>
            <IconButton 
              onClick={() => setTargetTemp(prev => Math.max(prev - 0.5, 15))}
              sx={{ 
                color: 'white', 
                bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Minus size={isMobile ? 24 : 28} />
            </IconButton>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ 
                fontWeight: 800, 
                minWidth: isMobile ? 100 : 140, 
                fontSize: isMobile ? '2.5rem' : '3.5rem',
                lineHeight: 1,
                letterSpacing: '-0.05em'
              }}>
                {targetTemp.toFixed(1)}°
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}>
                TARGET TEMP
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setTargetTemp(prev => Math.min(prev + 0.5, 30))}
              sx={{ 
                color: 'white', 
                bgcolor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Plus size={isMobile ? 24 : 28} />
            </IconButton>
          </Box>
          <Slider
            value={targetTemp}
            min={15}
            max={30}
            step={0.5}
            onChange={(e, val) => setTargetTemp(val)}
            sx={{ 
              width: '90%', 
              color: '#00d4ff',
              height: 8,
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
                backgroundColor: '#fff',
                border: '4px solid #00d4ff',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 8px rgba(0, 212, 255, 0.16)',
                },
              },
              '& .MuiSlider-rail': {
                opacity: 0.1,
                backgroundColor: '#fff',
              },
            }}
          />
          <Box sx={{ 
            p: 2, 
            borderRadius: '16px', 
            bgcolor: 'rgba(255,255,255,0.03)', 
            border: '1px solid rgba(255,255,255,0.05)',
            width: '100%',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
              Current Room Temperature: <span style={{ color: '#00d4ff', fontWeight: 700 }}>{formatSensorValue(selectedClimate?.attributes?.current_temperature) || '?'}°C</span>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: isMobile ? 3 : 4, 
          pt: 0,
          justifyContent: 'center',
          gap: 2
        }}>
          <Button 
            onClick={() => setClimateDialogOpen(false)} 
            sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600 }}
          >Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSetTemperature}
            sx={{ 
              bgcolor: '#fff', 
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' },
              borderRadius: '16px',
              px: isMobile ? 5 : 6,
              py: 1.5,
              fontSize: '1rem'
            }}
          >
            Apply Settings
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-animation {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default HomeDashboard;
